/**
 * Pure display helpers for the activity events table (round 16, 2026-08-16).
 *
 * • capDistinctSystems — the collapsed row's many-system overflow rule.
 * • mergeAuxEventsIntoParents — folds LEGACY standalone aux rows (memory
 *   updates recorded before the server-side fold shipped) into their parent
 *   task rows client-side, so history reads the same way new tasks are
 *   recorded: one task, one row.
 */

import type { ActivityEvent } from '../../services/rbacService';

/**
 * Collapsed rows stay one line: up to `maxInline` systems render in full;
 * past that, the first `shownWhenOver` plus a "+K more" marker (dropping
 * below the cap leaves the marker room). The expansion always carries the
 * complete wrapped list.
 */
export function capDistinctSystems<T>(
  distinct: T[],
  maxInline = 5,
  shownWhenOver = 4,
): { shown: T[]; hiddenCount: number } {
  if (distinct.length <= maxInline) return { shown: distinct, hiddenCount: 0 };
  return {
    shown: distinct.slice(0, shownWhenOver),
    hiddenCount: distinct.length - shownWhenOver,
  };
}

const MEMORY_AUX_PREFIX = 'memext_';
const PARENT_TURN_PREFIX = 'turn_';

/** Parent turn event id encoded in a legacy aux event id, or null. */
export function auxParentEventId(eventId: string | null | undefined): string | null {
  if (typeof eventId !== 'string') return null;
  if (!eventId.startsWith(MEMORY_AUX_PREFIX)) return null;
  const rest = eventId.slice(MEMORY_AUX_PREFIX.length);
  return rest ? `${PARENT_TURN_PREFIX}${rest}` : null;
}

/**
 * Fold legacy standalone memory-update rows into their parent task row when
 * both landed on the same page. `memext_{sid}_{tid}` and `turn_{sid}_{tid}`
 * differ only by prefix, and the update completes seconds after its task, so
 * the pair is virtually always adjacent. Amounts and tokens sum; the aux
 * call joins the parent's step list; the parent's own timing/summary stay.
 * Rows with no derivable parent on the page (old follow-up rows, orphaned
 * updates) pass through untouched. Events recorded after the server-side
 * fold shipped never mint aux rows, so this is history-only by design.
 */
export function mergeAuxEventsIntoParents(events: ActivityEvent[]): ActivityEvent[] {
  if (!Array.isArray(events) || events.length === 0) return events;
  const byId = new Map<string, ActivityEvent>();
  for (const e of events) {
    if (e && typeof e.eventId === 'string') byId.set(e.eventId, e);
  }

  const folded = new Map<string, ActivityEvent>();
  const dropped = new Set<string>();
  for (const e of events) {
    const parentId = auxParentEventId(e?.eventId);
    if (!parentId) continue;
    const parent = folded.get(parentId) ?? byId.get(parentId);
    if (!parent) continue;
    folded.set(parentId, {
      ...parent,
      inputTokens: (parent.inputTokens || 0) + (e.inputTokens || 0),
      outputTokens: (parent.outputTokens || 0) + (e.outputTokens || 0),
      reasoningTokens: (parent.reasoningTokens || 0) + (e.reasoningTokens || 0),
      totalTokens: (parent.totalTokens || 0) + (e.totalTokens || 0),
      amountUsd: (parent.amountUsd || 0) + (e.amountUsd || 0),
      amountOlv: (parent.amountOlv || 0) + (e.amountOlv || 0),
      // R20: the aux call's time rides the row as BACKGROUND time — the
      // task's own durationMs never includes it.
      ...(e.durationMs
        ? { auxDurationMs: (parent.auxDurationMs || 0) + e.durationMs }
        : {}),
      // The update ran after the task's own calls — its step goes last.
      ...(e.generations && e.generations.length > 0
        ? { generations: [...(parent.generations ?? []), ...e.generations] }
        : {}),
    });
    dropped.add(e.eventId);
  }
  if (dropped.size === 0) return events;

  return events
    .filter((e) => !dropped.has(e.eventId))
    .map((e) => folded.get(e.eventId) ?? e);
}
